import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDeskInPlan } from '../../hooks/use-desk-in-plan';
import { w } from '../../../stays/wording';
import styled from '@emotion/styled';
import { FormModal } from '../../modal/form-modal';
import { AppButton } from '../../button/app-button';
import { Stepper } from '../../stepper/stepper';
import { CountedTextarea } from '../../counted-textarea/counted-textarea';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../form-kit';
import { Shake, StatusLabel } from '../../../motion';
import toast from '../../../utils/toast';
import { addDays, todayIn, zoneLabel } from '../../../function-utils/building-time';
import { createReservation, StaysApiError } from '../../../api/stays-client';
import type { StayCreateInput, StayCreateResult, StaySource, StaysListing } from '../../../interfaces/stays';
import { refusalText, unitLine } from '../dashboard/dashboard-stays-components/stays-labels';
import { FormAlert } from '../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { SwitchLabel } from '../dashboard/dashboard-passes-components/dashboard-passes.styled';
import { palette, spacing, typography } from '../../../style-utils/theme';
import { isStayDayKey, nightsBetween, nightsText, rangeText, SOURCE_OPTIONS } from './stays-reservations-model';
import { StayLinkPanel } from './stay-link-panel';

/**
 * ADD A STAY TO A LISTING (ai-plans/stays plan S3) — the host types in a
 * booking taken elsewhere (direct, Airbnb, Booking.com…) and gets the guest's
 * check-in link.
 *
 * Dates are the BUILDING's calendar days: the check-in field's minimum is the
 * building's today as the server last answered it (the stay list's `today`),
 * falling back to today in the building's zone. The arrival time is the
 * building's wall clock; before 07:00 means the night after check-in (the desk
 * day starts at 07:00).
 *
 * One request per save: a ref guards a double press before React has
 * re-rendered the disabled button. The server's 400 field map lands on the
 * fields; an overlap lands on Check-in with the clashing dates (never the other
 * guest); a listing that is not live says so in a banner, in the server's words.
 */

export type AddStayListing = Pick<
  StaysListing,
  'id' | 'display_name' | 'max_guests' | 'check_in_time' | 'building_timezone' | 'building_name' | 'unit_number'
>;

export interface AddStayModalProps {
  open: boolean;
  listing: AddStayListing | null;
  /** The building's calendar date today, from the stay list response. */
  today?: string | null;
  onClose: () => void;
  onSaved: (result: StayCreateResult) => void;
}

type Form = {
  check_in: string;
  check_out: string;
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  lead_phone: string;
  party_size: number;
  expected_arrival_time: string;
  source: StaySource;
  external_uid: string;
  notes: string;
  send_email: boolean;
};

const EMPTY: Form = {
  check_in: '',
  check_out: '',
  lead_first_name: '',
  lead_last_name: '',
  lead_email: '',
  lead_phone: '',
  party_size: 1,
  expected_arrival_time: '',
  source: 'direct',
  external_uid: '',
  notes: '',
  send_email: false,
};

const FIELD_ORDER = [
  'check_in',
  'check_out',
  'lead_first_name',
  'lead_last_name',
  'lead_email',
  'lead_phone',
  'party_size',
  'expected_arrival_time',
  'source',
  'external_uid',
  'notes',
];

const NOTES_MAX = 1000;

const Nights = styled.p`
  margin: 0 0 ${spacing.sm};
  min-height: ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const SendRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin-top: ${spacing.sm};

  label[data-disabled='true'] {
    color: ${palette.slateLight};
    cursor: not-allowed;
  }
`;

export const overlapText = (conflict: unknown): string => {
  const c = conflict as { check_in?: unknown; check_out?: unknown } | null | undefined;
  if (c && isStayDayKey(c.check_in) && isStayDayKey(c.check_out)) {
    return `These dates overlap another stay (${rangeText(c.check_in, c.check_out)}).`;
  }

  return 'These dates overlap another stay.';
};

export const AddStayModal: React.FC<AddStayModalProps> = ({ open, listing, today, onClose, onSaved }) => {
  const uid = useId();
  const formId = `${uid}-add-stay`;
  const fid = (f: string) => `${uid}-${f}`;
  const eid = (f: string) => `${uid}-${f}-error`;
  const hid = (f: string) => `${uid}-${f}-hint`;

  const [form, setForm] = useState<Form>(EMPTY);
  const desk = useDeskInPlan();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setErrors({});
    setRefusal(null);
    setAttempt(0);
  }, [open, listing?.id]);

  const buildingToday = (today && isStayDayKey(today) ? today : '') || todayIn(listing?.building_timezone) || '';
  const checkOutMin = buildingToday ? addDays(form.check_in && isStayDayKey(form.check_in) ? form.check_in : buildingToday, 1) : '';
  const nights = nightsBetween(form.check_in, form.check_out);
  const city = zoneLabel(listing?.building_timezone);
  const maxGuests = Math.max(1, listing?.max_guests || 1);
  const hasEmail = form.lead_email.trim() !== '';
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(EMPTY), [form]);

  const update = (patch: Partial<Form>) => {
    setForm(current => {
      const next = { ...current, ...patch };
      // The send box means nothing without an address: clearing the email clears it.
      if (patch.lead_email !== undefined && patch.lead_email.trim() === '') next.send_email = false;

      return next;
    });
    setErrors(current => {
      const next = { ...current };
      for (const k of Object.keys(patch)) delete next[k];
      // Moving either date may settle an overlap or an order error on the other.
      if (patch.check_in !== undefined || patch.check_out !== undefined) {
        delete next.check_in;
        delete next.check_out;
      }

      return next;
    });
  };

  // The server's own words for its own checks (lobbero_be src/stays/reservations.js MSG).
  const localErrors = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!isStayDayKey(form.check_in)) e.check_in = 'Choose the check-in date.';
    if (!isStayDayKey(form.check_out)) e.check_out = 'Choose the check-out date.';
    else if (isStayDayKey(form.check_in) && form.check_out <= form.check_in) e.check_out = 'Check-out must be after check-in.';
    if (!form.lead_first_name.trim()) e.lead_first_name = "Enter the lead guest's first name.";
    if (!form.lead_last_name.trim()) e.lead_last_name = "Enter the lead guest's last name.";
    if (form.notes.length > NOTES_MAX) e.notes = 'Use at most 1000 characters.';

    return e;
  };

  const focusFirst = (errs: Record<string, string>, field?: string | null) => {
    const first = field && errs[field] ? field : FIELD_ORDER.find(f => errs[f]);
    if (!first) return;
    window.setTimeout(() => {
      const el = document.getElementById(fid(first));
      if (el) el.focus();
    }, 0);
  };

  const fail = (error: unknown) => {
    setAttempt(a => a + 1);
    if (error instanceof StaysApiError && error.status === 400 && Object.keys(error.fields).length) {
      setErrors(error.fields);
      focusFirst(error.fields, error.field);

      return;
    }
    if (error instanceof StaysApiError && error.code === 'STAY_OVERLAP') {
      const errs = { check_in: overlapText(error.extra.conflict) };
      setErrors(errs);
      focusFirst(errs);

      return;
    }
    setRefusal(refusalText(error as StaysApiError, 'Could not save the stay. Try again.'));
  };

  const submit = async () => {
    if (busy.current || !listing) return;
    setRefusal(null);
    const errs = localErrors();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setAttempt(a => a + 1);
      focusFirst(errs);

      return;
    }
    busy.current = true;
    setSaving(true);
    const email = form.lead_email.trim();
    const phone = form.lead_phone.trim();
    const ref = form.external_uid.trim();
    const notes = form.notes.trim();
    const input: StayCreateInput = {
      check_in: form.check_in,
      check_out: form.check_out,
      lead_first_name: form.lead_first_name.trim(),
      lead_last_name: form.lead_last_name.trim(),
      party_size: form.party_size,
      source: form.source,
      ...(email ? { lead_email: email } : {}),
      ...(phone ? { lead_phone: phone } : {}),
      ...(form.expected_arrival_time ? { expected_arrival_time: form.expected_arrival_time } : {}),
      ...(ref ? { external_uid: ref } : {}),
      ...(notes ? { notes } : {}),
      send_email: !!email && form.send_email,
    };
    try {
      const result = await createReservation(listing.id, input);
      toast.success(`Stay saved for ${result.reservation.lead.first_name || input.lead_first_name}.`);
      onSaved(result);
    } catch (error) {
      fail(error);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const describe = (f: string, hint?: boolean) => (errors[f] ? eid(f) : hint ? hid(f) : undefined);

  return (
    <FormModal
      open={open}
      title={`Add a stay to ${listing?.display_name || 'this listing'}`}
      subtitle={listing ? unitLine({ unit_number: listing.unit_number, building_name: listing.building_name }, desk) : undefined}
      onClose={onClose}
      isDirty={dirty}
      saving={saving}
      discardMessage="This stay has not been saved. Close and lose what you have entered?"
      footer={
        <>
          <AppButton variant="secondary" type="button" disabled={saving} onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton type="submit" form={formId} disabled={saving}>
            <StatusLabel state={saving ? 'busy' : 'idle'} labels={{ idle: 'Save stay', busy: 'Saving…' }} />
          </AppButton>
        </>
      }
    >
      <Shake active={attempt > 0 && (Object.keys(errors).length > 0 || !!refusal)} attempt={attempt}>
        <form
          id={formId}
          noValidate
          onSubmit={event => {
            event.preventDefault();
            void submit();
          }}
        >
          {refusal ? <FormAlert role="alert">{refusal}</FormAlert> : null}

          <FieldRow>
            <Field>
              <Label htmlFor={fid('check_in')}>Check-in</Label>
              <Input
                id={fid('check_in')}
                type="date"
                value={form.check_in}
                min={buildingToday || undefined}
                aria-invalid={errors.check_in ? true : undefined}
                aria-describedby={describe('check_in')}
                hasError={!!errors.check_in}
                disabled={saving}
                data-autofocus
                onChange={event => update({ check_in: event.target.value })}
              />
              <FieldError id={eid('check_in')} message={errors.check_in} />
            </Field>
            <Field>
              <Label htmlFor={fid('check_out')}>Check-out</Label>
              <Input
                id={fid('check_out')}
                type="date"
                value={form.check_out}
                min={checkOutMin || undefined}
                aria-invalid={errors.check_out ? true : undefined}
                aria-describedby={describe('check_out')}
                hasError={!!errors.check_out}
                disabled={saving}
                onChange={event => update({ check_out: event.target.value })}
              />
              <FieldError id={eid('check_out')} message={errors.check_out} />
            </Field>
          </FieldRow>
          <Nights role="status" data-testid="add-stay-nights">
            {nights ? nightsText(nights) : ''}
          </Nights>

          <FieldRow>
            <Field>
              <Label htmlFor={fid('lead_first_name')}>Lead guest first name</Label>
              <Input
                id={fid('lead_first_name')}
                value={form.lead_first_name}
                maxLength={80}
                autoComplete="off"
                aria-invalid={errors.lead_first_name ? true : undefined}
                aria-describedby={describe('lead_first_name')}
                hasError={!!errors.lead_first_name}
                disabled={saving}
                onChange={event => update({ lead_first_name: event.target.value })}
              />
              <FieldError id={eid('lead_first_name')} message={errors.lead_first_name} />
            </Field>
            <Field>
              <Label htmlFor={fid('lead_last_name')}>Last name</Label>
              <Input
                id={fid('lead_last_name')}
                value={form.lead_last_name}
                maxLength={80}
                autoComplete="off"
                aria-invalid={errors.lead_last_name ? true : undefined}
                aria-describedby={describe('lead_last_name')}
                hasError={!!errors.lead_last_name}
                disabled={saving}
                onChange={event => update({ lead_last_name: event.target.value })}
              />
              <FieldError id={eid('lead_last_name')} message={errors.lead_last_name} />
            </Field>
          </FieldRow>

          <FieldRow>
            <Field>
              <Label htmlFor={fid('lead_email')}>Email (optional)</Label>
              <Input
                id={fid('lead_email')}
                type="email"
                inputMode="email"
                value={form.lead_email}
                maxLength={254}
                autoComplete="off"
                aria-invalid={errors.lead_email ? true : undefined}
                aria-describedby={describe('lead_email', true)}
                hasError={!!errors.lead_email}
                disabled={saving}
                onChange={event => update({ lead_email: event.target.value })}
              />
              <FieldHint id={hid('lead_email')}>We send the check-in link here.</FieldHint>
              <FieldError id={eid('lead_email')} message={errors.lead_email} />
            </Field>
            <Field>
              <Label htmlFor={fid('lead_phone')}>Phone (optional)</Label>
              <Input
                id={fid('lead_phone')}
                type="tel"
                inputMode="tel"
                value={form.lead_phone}
                maxLength={32}
                autoComplete="off"
                aria-invalid={errors.lead_phone ? true : undefined}
                aria-describedby={describe('lead_phone')}
                hasError={!!errors.lead_phone}
                disabled={saving}
                onChange={event => update({ lead_phone: event.target.value })}
              />
              <FieldError id={eid('lead_phone')} message={errors.lead_phone} />
            </Field>
          </FieldRow>

          <FieldRow>
            <Field>
              <Label id={fid('party_size-label')}>Guests</Label>
              <Stepper
                value={Math.min(form.party_size, maxGuests)}
                min={1}
                max={maxGuests}
                label="guests"
                disabled={saving}
                onChange={value => update({ party_size: value })}
              />
              <FieldError id={eid('party_size')} message={errors.party_size} />
            </Field>
            <Field>
              <Label htmlFor={fid('expected_arrival_time')}>Expected arrival (optional)</Label>
              <Input
                id={fid('expected_arrival_time')}
                type="time"
                value={form.expected_arrival_time}
                aria-invalid={errors.expected_arrival_time ? true : undefined}
                aria-describedby={describe('expected_arrival_time', true)}
                hasError={!!errors.expected_arrival_time}
                disabled={saving}
                onChange={event => update({ expected_arrival_time: event.target.value })}
              />
              <FieldHint id={hid('expected_arrival_time')}>
                {city
                  ? `${w('buildingTime', desk)} (${city}). Before 07:00 means the night after check-in.`
                  : `${w('buildingTime', desk)}. Before 07:00 means the night after check-in.`}
              </FieldHint>
              <FieldError id={eid('expected_arrival_time')} message={errors.expected_arrival_time} />
            </Field>
          </FieldRow>

          <FieldRow>
            <Field>
              <Label htmlFor={fid('source')}>Booked through</Label>
              <Select
                id={fid('source')}
                value={form.source}
                aria-invalid={errors.source ? true : undefined}
                aria-describedby={describe('source')}
                disabled={saving}
                onChange={event => update({ source: event.target.value as StaySource })}
              >
                {SOURCE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <FieldError id={eid('source')} message={errors.source} />
            </Field>
            <Field>
              <Label htmlFor={fid('external_uid')}>Booking reference (optional)</Label>
              <Input
                id={fid('external_uid')}
                value={form.external_uid}
                maxLength={255}
                autoComplete="off"
                aria-invalid={errors.external_uid ? true : undefined}
                aria-describedby={describe('external_uid')}
                hasError={!!errors.external_uid}
                disabled={saving}
                onChange={event => update({ external_uid: event.target.value })}
              />
              <FieldError id={eid('external_uid')} message={errors.external_uid} />
            </Field>
          </FieldRow>

          <Field>
            <Label htmlFor={fid('notes')}>Notes for the team (optional)</Label>
            <CountedTextarea
              id={fid('notes')}
              value={form.notes}
              max={NOTES_MAX}
              rows={3}
              hasError={!!errors.notes}
              aria-describedby={errors.notes ? eid('notes') : undefined}
              disabled={saving}
              onChange={event => update({ notes: event.target.value })}
            />
            <FieldError id={eid('notes')} message={errors.notes} />
          </Field>

          <SendRow>
            <SwitchLabel data-disabled={!hasEmail || saving ? 'true' : undefined}>
              <input
                type="checkbox"
                id={fid('send_email')}
                checked={hasEmail && form.send_email}
                disabled={!hasEmail || saving}
                onChange={event => update({ send_email: event.target.checked })}
              />
              Email the check-in link to the guest now
            </SwitchLabel>
            <FieldError id={eid('send_email')} message={errors.send_email} />
          </SendRow>
        </form>
      </Shake>
    </FormModal>
  );
};

/**
 * THE WHOLE ADD FLOW — the form, then the once-only link. The page mounts this
 * once and passes the listing to add to; null closes it.
 */
export const AddStayFlow: React.FC<{
  listing: AddStayListing | null;
  today?: string | null;
  onClose: () => void;
  /** A stay was saved: the list reads again. */
  onSaved?: (result: StayCreateResult) => void;
}> = ({ listing, today, onClose, onSaved }) => {
  const [saved, setSaved] = useState<StayCreateResult | null>(null);
  // One object per save, so the panel's open-reset runs once and not on every render.
  const issued = useMemo(() => (saved ? { link: saved.link, email: saved.email } : null), [saved]);

  useEffect(() => {
    if (listing) setSaved(null);
  }, [listing]);

  return (
    <>
      <AddStayModal
        open={!!listing && !saved}
        listing={listing}
        today={today}
        onClose={onClose}
        onSaved={result => {
          setSaved(result);
          if (onSaved) onSaved(result);
        }}
      />
      <StayLinkPanel
        open={!!saved}
        reservation={saved ? saved.reservation : null}
        issued={issued}
        onClose={() => {
          setSaved(null);
          onClose();
        }}
      />
    </>
  );
};
